import { createApi } from '@reduxjs/toolkit/query/react';
import { Response } from '../../models/shared';
import {
  OpenedScannerSessionDTO,
  PersonSearchRowDTO,
  ScanOrderResultDTO,
  ScanResultDTO,
  ScanSource,
  ScannerAccessDTO,
  ScannerActivityDTO,
  ScannerDoorDTO,
  ScannerEventDTO,
  ScannerSessionDTO,
  ScannerTodayEventDTO,
} from '../../models/scanner';
import scannerBaseQuery from './scanner-base';
import baseQueryWithReauth from './base';

// Every endpoint here answers { data }: an empty body is an error, not "no data".
const unwrap = <T>(response: Response<T>): T => {
  if (response.data === undefined) {
    throw new Error('Respuesta vacía del servidor');
  }
  return response.data;
};

export interface OpenScannerSessionArgs {
  token?: string;
  code?: string;
  name: string;
  device_id: string;
}

export interface CreateScanArgs {
  eventId: string;
  scan_id: string;
  source: ScanSource;
  data?: string;
  item_id?: string;
  device_at: string;
  device_id: string;
}

// The scanner itself: an account or a code session (see scanner-base).
export const scannerApi = createApi({
  reducerPath: 'scannerApi',
  baseQuery: scannerBaseQuery,
  tagTypes: ['scanner-summary'],
  endpoints: (build) => ({
    openScannerSession: build.mutation<OpenedScannerSessionDTO, OpenScannerSessionArgs>({
      query: (body) => ({ url: 'scanner-sessions', method: 'POST', body }),
      transformResponse: (response: Response<OpenedScannerSessionDTO>) => unwrap(response),
    }),
    previewScannerSession: build.mutation<{ event: ScannerEventDTO; door: ScannerDoorDTO | null }, Omit<OpenScannerSessionArgs, 'name'>>({
      query: (body) => ({ url: 'scanner-sessions/preview', method: 'POST', body }),
      transformResponse: (response: Response<{ event: ScannerEventDTO; door: ScannerDoorDTO | null }>) => unwrap(response),
    }),
    getCurrentScannerSession: build.query<{ session: ScannerSessionDTO; event: ScannerEventDTO; door: ScannerDoorDTO | null }, void>({
      query: () => ({ url: 'scanner-sessions/current' }),
      transformResponse: (response: Response<{ session: ScannerSessionDTO; event: ScannerEventDTO; door: ScannerDoorDTO | null }>) => unwrap(response),
      keepUnusedDataFor: 0,
    }),
    getTodayScannerEvents: build.query<ScannerTodayEventDTO[], void>({
      query: () => ({ url: 'me/scanner-events/today' }),
      transformResponse: (response: Response<ScannerTodayEventDTO[]>) => unwrap(response),
      keepUnusedDataFor: 0,
    }),
    // The event and door an account scans, for the scanner opened from the panel of one event.
    getScannerEvent: build.query<{ event: ScannerEventDTO; door: ScannerDoorDTO | null }, string>({
      query: (eventId) => ({ url: `events/${eventId}/scanner-event` }),
      transformResponse: (response: Response<{ event: ScannerEventDTO; door: ScannerDoorDTO | null }>) => unwrap(response),
      keepUnusedDataFor: 0,
    }),
    // sessionKey only keys the cache: one session's error is not the next one's.
    getScanSummary: build.query<{ entries: number }, { eventId: string; sessionKey: string }>({
      query: ({ eventId }) => ({ url: `events/${eventId}/scans/summary` }),
      transformResponse: (response: Response<{ entries: number }>) => unwrap(response),
      providesTags: ['scanner-summary'],
    }),
    createScan: build.mutation<ScanResultDTO, CreateScanArgs>({
      query: ({ eventId, ...body }) => ({ url: `events/${eventId}/scans`, method: 'POST', body }),
      transformResponse: (response: Response<ScanResultDTO>) => unwrap(response),
    }),
    confirmScan: build.mutation<ScanResultDTO, { eventId: string; scanId: string; override?: boolean; device_id: string }>({
      query: ({ eventId, scanId, ...body }) => ({ url: `events/${eventId}/scans/${scanId}/confirm`, method: 'POST', body }),
      transformResponse: (response: Response<ScanResultDTO>) => unwrap(response),
    }),
    orderScan: build.mutation<ScanOrderResultDTO, { eventId: string; scanId: string; item_ids: string[]; device_id: string }>({
      query: ({ eventId, scanId, ...body }) => ({ url: `events/${eventId}/scans/${scanId}/order`, method: 'POST', body }),
      transformResponse: (response: Response<ScanOrderResultDTO>) => unwrap(response),
    }),
    searchPerson: build.query<PersonSearchRowDTO[], { eventId: string; personId: string }>({
      query: ({ eventId, personId }) => ({ url: `events/${eventId}/person-search`, params: { person_id: personId } }),
      transformResponse: (response: Response<PersonSearchRowDTO[]>) => unwrap(response),
      keepUnusedDataFor: 0,
    }),
  }),
});

// The producer's panel ("Sumar escáneres"), always with the account.
export const scannerPanelApi = createApi({
  reducerPath: 'scannerPanelApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['scanner-access', 'scanner-activity'],
  endpoints: (build) => ({
    getScannerAccess: build.query<ScannerAccessDTO, { businessId: string; eventId: string; doorId: string | null }>({
      query: ({ businessId, eventId, doorId }) => ({
        url: `businesses/${businessId}/events/${eventId}/scanner-access`,
        params: doorId ? { staff_door_id: doorId } : undefined,
      }),
      transformResponse: (response: Response<ScannerAccessDTO>) => unwrap(response),
      providesTags: ['scanner-access'],
    }),
    rotateScannerAccess: build.mutation<ScannerAccessDTO, { businessId: string; eventId: string; doorId: string | null }>({
      query: ({ businessId, eventId, doorId }) => ({
        url: `businesses/${businessId}/events/${eventId}/scanner-access/rotate`,
        method: 'POST',
        body: { staff_door_id: doorId },
      }),
      transformResponse: (response: Response<ScannerAccessDTO>) => unwrap(response),
      invalidatesTags: ['scanner-access', 'scanner-activity'],
    }),
    getScannerActivity: build.query<ScannerActivityDTO[], { businessId: string; eventId: string }>({
      query: ({ businessId, eventId }) => ({ url: `businesses/${businessId}/events/${eventId}/scanner-sessions` }),
      transformResponse: (response: Response<ScannerActivityDTO[]>) => unwrap(response),
      providesTags: ['scanner-activity'],
    }),
    endScannerSession: build.mutation<unknown, { businessId: string; eventId: string; sessionId: string }>({
      query: ({ businessId, eventId, sessionId }) => ({
        url: `businesses/${businessId}/events/${eventId}/scanner-sessions/${sessionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['scanner-access', 'scanner-activity'],
    }),
  }),
});

export const {
  useOpenScannerSessionMutation,
  usePreviewScannerSessionMutation,
  useGetCurrentScannerSessionQuery,
  useGetTodayScannerEventsQuery,
  useGetScannerEventQuery,
  useGetScanSummaryQuery,
  useCreateScanMutation,
  useConfirmScanMutation,
  useOrderScanMutation,
  useLazySearchPersonQuery,
} = scannerApi;

export const {
  useGetScannerAccessQuery,
  useRotateScannerAccessMutation,
  useGetScannerActivityQuery,
  useEndScannerSessionMutation,
} = scannerPanelApi;
